import { baseApi } from 'src/Services/api.ts'
import { PaginatedResponse } from 'src/Redux/types.ts'
import { VatVisibilityEnum } from 'src/Pages/Calculator/helpers.ts'

import { Client } from './clientsApi'
import { Model } from './modelApi'

export interface OfferModelUnit {
  id?: number
  departurePoint?: string
  workPoint?: string
  returnPoint?: string
  deliveryCost?: number
  returnCost?: number
  quantity?: number
  shiftCount?: number
  toolId?: number
  totalRentPricePerHour?: number
}

export interface OfferModel {
  id: number
  offerId: number
  modelId: number
  model: Model
  toolId?: number
  route?: string
  relocationCost?: number
  count?: number
  specialPrice?: number
  shiftsCount?: number
  totalRentPricePerHour?: number
  isDetalized?: boolean
  selectedImageUrl?: string
  units?: OfferModelUnit[]
}

export interface User {
  id: number
  firstName?: string
  lastName?: string
  email: string
}

export interface Offer {
  id: number
  clientId: number
  client: Client
  userId: number
  user: User
  offerNumber: string
  clientContactName?: string
  clientContactNamePosition?: string
  clientDivision?: string
  fuelPrice?: number
  deliveryPrice?: number
  personDeliveryPrice?: number
  accommodationPrice?: number
  foodPrice?: number
  personnelHouseholdServicesCost?: number
  dailyMedCheckCost?: number
  minRentPeriod?: number
  personalBenefits?: boolean
  vatVisibility?: VatVisibilityEnum
  createDate?: string
  validityDaysCount?: number
  season?: string
  createdAt: string
  offerModels?: OfferModel[]
  contractNumber?: string
}

export interface OfferQueryParams {
  pageNumber?: number
  limit?: number
  searchString?: string
  clientId?: number
  managerId?: number
}

export interface CreateOfferBody {
  clientId: number
  userId: number
  clientContactName?: string
  clientContactNamePosition?: string
  clientDivision?: string
  fuelPrice?: number
  deliveryPrice?: number
  personDeliveryPrice?: number
  accommodationPrice?: number
  foodPrice?: number
  personnelHouseholdServicesCost?: number
  dailyMedCheckCost?: number
  minRentPeriod?: number
  personalBenefits?: boolean
  vatVisibility?: VatVisibilityEnum
  createDate?: string
  validityDaysCount?: number
  season?: string
  models: {
    id: number
    toolId?: number
    route?: string
    relocationCost?: number
    count?: number
    specialPrice?: number
    shiftsCount?: number
    totalRentPricePerHour?: number
    isDetalized?: boolean
    selectedImageUrl?: string
    units?: OfferModelUnit[]
  }[]
}

export const offersApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAllOffers: builder.query<PaginatedResponse<Offer[]>, OfferQueryParams>({
      query: (params) => {
        const queryParams = new URLSearchParams()

        if (params) {
          if (params.pageNumber)
            queryParams.append('page', params.pageNumber.toString())
          if (params.limit) queryParams.append('limit', params.limit.toString())
          if (params.searchString)
            queryParams.append('searchTerm', params.searchString)
          if (params.clientId)
            queryParams.append('clientId', params.clientId.toString())
          if (params.managerId)
            queryParams.append('managerId', params.managerId.toString())
        }

        const queryString = queryParams.toString()
        return `/offers${queryString ? `?${queryString}` : ''}`
      },

      serializeQueryArgs: ({ endpointName }) => endpointName,

      merge: (currentCache, newItems, meta) => {
        if (meta.arg.pageNumber === 1) {
          return {
            data: newItems?.data,
            meta: newItems.meta,
          }
        }
        return {
          data: [...currentCache.data, ...newItems.data],
          meta: newItems.meta,
        }
      },
      forceRefetch({ currentArg, previousArg }) {
        return JSON.stringify(currentArg) !== JSON.stringify(previousArg)
      },

      providesTags: ['offers'],
    }),

    getOfferById: builder.query<Offer, number>({
      query: (id) => `/offers/${id}`,
      providesTags: ['offers'],
    }),

    createOffer: builder.mutation<Offer, CreateOfferBody>({
      query: (data) => ({
        url: `/offers`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['offers'],
    }),

    updateOffer: builder.mutation<Offer, { id: number; data: CreateOfferBody }>(
      {
        query: ({ id, data }) => ({
          url: `/offers/${id}`,
          method: 'PUT',
          body: data,
        }),
        invalidatesTags: ['offers'],
      },
    ),

    deleteOffer: builder.mutation<void, number>({
      query: (id) => ({
        url: `/offers/${id}`,
        method: 'DELETE',
        body: {},
      }),
      invalidatesTags: ['offers'],
    }),

    cloneOffer: builder.mutation<Offer, number>({
      query: (id) => ({
        url: `/offers/${id}/clone`,
        method: 'POST',
        body: {},
      }),
      invalidatesTags: ['offers'],
    }),
  }),
  overrideExisting: true,
})

export const {
  useGetAllOffersQuery,
  useGetOfferByIdQuery,
  useCreateOfferMutation,
  useUpdateOfferMutation,
  useDeleteOfferMutation,
  useCloneOfferMutation,
} = offersApi
